'use client'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { Badge, Button, Card } from './ui'
import { masjidPlaceholder } from '../lib/masjid-placeholder'
import { imageUrl } from '../lib/image-system'

// ============================================
// MASJID CARD
// ============================================
interface Masjid {
  id: string
  slug: string
  nameEnglish: string
  nameUrdu: string | null
  city: string | null
  state: string | null
  sect: string | null
  verified: boolean | null
  coverPhoto: string | null
  photoReady?: boolean | null
  facilities: string[] | null
  prayerTimes: unknown
}

export function MasjidCard({ masjid, featured = false }: { masjid: Masjid; featured?: boolean }) {
  /* Photo resolution mirrors MasjidKitCard (kit.tsx): uploaded coverPhoto first, else the
     Google photo proxy — previously this card NEVER called the proxy, so every consumer showed
     a placeholder for all but the handful of rows with uploaded photos.

     Quality (homepage card):
     - Uploaded Cloudinary covers go through imageUrl(..,'masjidCard') so the c_limit,w_1280
       rendition is served (raw covers used to ship old small c_fill URLs bypassing that work).
     - The Google proxy is requested at w=1280 (was the proxy's 800 default — too small for a
       ~396px card at 2×) with a srcset so retina gets a real 2× rendition. srcset (not
       next/image) because the source is a same-origin dynamic proxy that already returns
       processed, heavily-cached bytes — next/image would double-process and need loader config
       for a route the rest of the app renders with a plain <img>.
     A 404 still falls back to the branded placeholder via onError. Never stock/borrowed imagery. */
  const uploaded = masjid.coverPhoto ? imageUrl(masjid.coverPhoto, 'masjidCard') : null
  const usingProxy = !uploaded && masjid.photoReady !== false && !!masjid.id
  const proxy = (w: number) => `/api/masjids/${masjid.id}/photo?w=${w}`
  const cover = uploaded || (usingProxy ? proxy(1280) : masjidPlaceholder(masjid.slug))
  const srcSet = usingProxy ? `${proxy(640)} 640w, ${proxy(1280)} 1280w` : undefined
  return (
    <Link href={`/masjids/${masjid.slug}`}>
      <Card padding="none" interactive className="h-full">
        {/* Cover image — taller band (h-48) than the old h-40 so a landscape photo isn't
            cropped to a thin strip; closer to the 16:10 card ratio. */}
        <div className="h-48 relative">
          <img
            src={cover}
            srcSet={srcSet}
            sizes="(max-width: 768px) 76vw, 340px"
            alt={masjid.nameEnglish}
            className="w-full h-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = masjidPlaceholder(masjid.slug) }}
          />
          {/* Curated distinction: a gold FEATURED badge marks the admin-picked row apart from
              the newest-added Latest row; the verified tick stays on its own corner. */}
          {featured && (
            <Badge tone="gold" className="absolute top-3 left-3">
              ★ Featured
            </Badge>
          )}
          {masjid.verified && (
            <Badge tone="success" className="absolute top-3 right-3">
              ✓ Verified
            </Badge>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 text-sm leading-snug">{masjid.nameEnglish}</h3>
          {masjid.nameUrdu && (
            <p className="urdu text-xs text-primary-600 mt-0.5">{masjid.nameUrdu}</p>
          )}

          <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
            <span>📍</span>
            <span>{[masjid.city, masjid.state].filter(Boolean).join(', ')}</span>
          </div>

          {masjid.sect && (
            <div className="mt-2">
              <Badge tone="neutral" className="capitalize">{masjid.sect}</Badge>
            </div>
          )}

          {masjid.facilities && masjid.facilities.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {masjid.facilities.slice(0, 3).map((f) => (
                <Badge key={f} tone="neutral">{f}</Badge>
              ))}
              {masjid.facilities.length > 3 && (
                <span className="text-xs text-gray-400">+{masjid.facilities.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </Card>
    </Link>
  )
}

// ============================================
// CAMPAIGN CARD
// ============================================
interface Campaign {
  id: string
  titleEnglish: string | null
  titleUrdu: string | null
  storyEnglish: string | null
  goalAmount: string
  raisedAmount: string | null
  status: string | null
  campaignType: string | null
  endsAt: Date | null
  masjid?: Masjid | null
}

export function CampaignCard({ campaign }: { campaign: Campaign }) {
  // ⚠ MONEY CORRECTNESS: `campaign.raisedAmount` must be the COMPUTED value (SUM of real
  // completed donations — src/lib/campaign-raised.ts, or the already-derived GET /api/campaigns),
  // never the stored campaigns.raisedAmount column. This is a CLIENT component and cannot call
  // the server-only helper itself, so the derived value must arrive from the caller. (Currently
  // this card is only rendered in /design-system with static props.) Bar gated on raised > 0.
  const raised = parseFloat(campaign.raisedAmount || '0')
  const goal = parseFloat(campaign.goalAmount)
  const percent = Math.min(100, Math.round((raised / goal) * 100))

  const daysLeft = campaign.endsAt
    ? Math.max(0, Math.ceil((new Date(campaign.endsAt).getTime() - Date.now()) / 86400000))
    : null

  return (
    <Link href={`/campaign/${campaign.id}`}>
      <Card padding="none" interactive className="h-full">
        <div className="h-36 bg-gradient-to-br from-primary-700 to-gold-600 flex items-center justify-center relative">
          <span className="text-5xl opacity-50">💚</span>
          {campaign.campaignType && (
            <Badge tone="gold" className="absolute top-3 left-3 capitalize">{campaign.campaignType}</Badge>
          )}
        </div>

        <div className="p-4">
          <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-0.5">
            {campaign.titleEnglish}
          </h3>
          {campaign.titleUrdu && (
            <p className="urdu text-xs text-primary-600 mb-2">{campaign.titleUrdu}</p>
          )}

          {campaign.masjid && (
            <p className="text-xs text-gray-500 mb-3">
              🕌 {campaign.masjid.nameEnglish} — {campaign.masjid.city}
            </p>
          )}

          {/* Progress bar + % only once real money is in; at ₹0 show the target as plain text. */}
          {raised > 0 ? (
            <>
              <div className="progress-bar mb-2">
                <div className="progress-fill" style={{ width: `${percent}%` }} />
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>
                  <strong className="text-primary-700">₹{raised.toLocaleString('en-IN')}</strong>
                  <span className="text-gray-400"> raised</span>
                </span>
                <span className="text-gray-500">{percent}% of ₹{goal.toLocaleString('en-IN')}</span>
              </div>
            </>
          ) : (
            <div className="text-xs text-gray-500">Target: ₹{goal.toLocaleString('en-IN')}</div>
          )}

          {daysLeft !== null && (
            <p className="text-xs text-gray-400 mt-2">{daysLeft} days left</p>
          )}

          <div className="mt-3 ui-button ui-button-primary ui-button-sm w-full">
            Donate Now — عطیہ دیں
          </div>
        </div>
      </Card>
    </Link>
  )
}

// ============================================
// SEARCH BAR
// ============================================
export function SearchBar() {
  const router = useRouter()
  const [query, setQuery] = useState('')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      router.push(`/directory?q=${encodeURIComponent(query)}`)
    }
  }

  return (
    <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
      <div className="flex gap-2 bg-white rounded-2xl p-2 shadow-xl">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by city, masjid name... | شہر، مسجد کا نام"
          className="flex-1 px-4 py-2 text-gray-900 bg-transparent outline-none text-sm"
        />
        <Button
          type="submit"
        >
          Search 🔍
        </Button>
      </div>
    </form>
  )
}

// ============================================
// STATS BAR
// ============================================
export function StatsBar({ stats }: { stats: { masjids: number; campaigns: number } }) {
  return (
    <div className="bg-primary-800 text-white py-3">
      <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-8 text-sm">
        <span>🕌 <strong>{stats.masjids}</strong> Verified Masjids</span>
        <span>💚 <strong>{stats.campaigns}</strong> Active Campaigns</span>
        <span>🛡️ Masjid Waqf Suite Available</span>
        <span className="urdu text-primary-200">AI سے اردو میں تیار</span>
      </div>
    </div>
  )
}

// ============================================
// RISK SCORE BADGE
// ============================================
export function RiskScoreBadge({ score }: { score: number | null }) {
  if (score === null) return <Badge tone="neutral">Not scored</Badge>

  const tone = score <= 3 ? 'success'
    : score <= 8 ? 'warning'
    : 'danger'

  const label = score <= 3 ? 'Low Risk' : score <= 6 ? 'Medium Risk' : score <= 8 ? 'High Risk' : 'Critical'

  return (
    <Badge tone={tone}>
      {score}/10 — {label}
    </Badge>
  )
}

// ============================================
// LANGUAGE TOGGLE BUTTON
// ============================================
export function LangToggle({
  lang,
  onToggle,
}: {
  lang: 'en' | 'ur'
  onToggle: () => void
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={onToggle}
    >
      {lang === 'en' ? 'اردو' : 'English'}
    </Button>
  )
}

// ============================================
// LOADING SPINNER
// ============================================
export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const s = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-10 h-10' : 'w-6 h-6'
  return (
    <div className={`${s} border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin`} />
  )
}

// ============================================
// EMPTY STATE
// ============================================
export function EmptyState({
  icon = '📭',
  title,
  titleUr,
  action,
}: {
  icon?: string
  title: string
  titleUr?: string
  action?: React.ReactNode
}) {
  return (
    <div className="text-center py-16">
      <div className="text-5xl mb-4">{icon}</div>
      <p className="text-gray-500 font-medium">{title}</p>
      {titleUr && <p className="urdu text-gray-400 text-sm mt-1">{titleUr}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}
